import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiWithAuth from '../../services/apiWithAuth';
import {
    Users,
    Mail,
    ShoppingCart,
    TrendingUp,
    Download,
    Smartphone,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    Package,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle
} from 'lucide-react';

const CTODashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        contactUsCount: 0,
        totalOrders: 0,
        kdpInstalls: 0,
        kdInstalls: 0,
        kdpAndroidInstalls: 0,
        kdAndroidInstalls: 0,
        kdIOSInstalls: 0,
        kdpIOSInstalls: 0,
        webUsers: 0,
        totalInstalls: 0
    });
    const [recentOrders, setRecentOrders] = useState([]);
    const [ordersExpanded, setOrdersExpanded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch Contact Us Count
    const fetchContactUsCount = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/website-main/contact-us?page=1&limit=1`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json();
            if (data.success) {
                return data.data.pagination.totalCount || 0;
            }
            return 0;
        } catch (err) {
            console.error('Error fetching contact us count:', err);
            return 0;
        }
    };

    // Fetch Orders Count and Recent Orders
    const fetchOrdersData = async () => {
        try {
            const response = await apiWithAuth.get('/admin-panel/orders-and-invoices/orders', {
                params: { page: 1, limit: 5 }
            });
            const data = response.data;
            return {
                totalOrders: data.totalOrders || 0,
                recentOrders: data.orders || []
            };
        } catch (err) {
            console.error('Error fetching orders:', err);
            return { totalOrders: 0, recentOrders: [] };
        }
    };

    // Fetch Firebase App Install Counts
    const fetchAppInstalls = async () => {
        try {
            const response = await apiWithAuth.get('/analytics/app-installs');
            const data = response.data;
            
            if (data.success) {
                return {
                    kdpInstalls: data.data.totalKDPInstalls || 0,
                    kdInstalls: data.data.totalKDInstalls || 0,
                    kdpAndroidInstalls: data.data.kdpAndroidInstalls || 0,
                    kdAndroidInstalls: data.data.kdAndroidInstalls || 0,
                    kdIOSInstalls: data.data.kdIOSInstalls || 0,
                    kdpIOSInstalls: data.data.kdpIOSInstalls || 0,
                    webUsers: data.data.webUsers || 0,
                    totalInstalls: data.data.totalInstalls || 0
                };
            }
            return {
                kdpInstalls: 0,
                kdInstalls: 0
            };
        } catch (err) {
            console.error('Error fetching app installs:', err);
            return {
                kdpInstalls: 0,
                kdInstalls: 0
            };
        }
    };

    useEffect(() => {
        const loadDashboard = async () => {
            setLoading(true);
            try {
                const [contactCount, ordersData, appInstalls] = await Promise.all([
                    fetchContactUsCount(),
                    fetchOrdersData(),
                    fetchAppInstalls()
                ]);

                setStats({
                    contactUsCount: contactCount,
                    totalOrders: ordersData.totalOrders,
                    kdpInstalls: appInstalls.kdpInstalls,
                    kdInstalls: appInstalls.kdInstalls,
                    kdpAndroidInstalls: appInstalls.kdpAndroidInstalls || 0,
                    kdAndroidInstalls: appInstalls.kdAndroidInstalls || 0,
                    kdIOSInstalls: appInstalls.kdIOSInstalls || 0,
                    kdpIOSInstalls: appInstalls.kdpIOSInstalls || 0,
                    webUsers: appInstalls.webUsers || 0,
                    totalInstalls: appInstalls.totalInstalls || 0
                });

                setRecentOrders(ordersData.recentOrders);
            } catch (err) {
                setError('Failed to load dashboard data');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadDashboard();
    }, []);

    const getStatusColor = (status) => {
        if (!status) return 'bg-gray-100 text-gray-800';
        const colors = {
            pending_shop_confirmation: 'bg-yellow-100 text-yellow-800',
            shop_confirmed: 'bg-blue-100 text-blue-800',
            shop_rejected: 'bg-red-100 text-red-800',
            stock_updated: 'bg-gray-100 text-gray-800',
            user_accepted: 'bg-green-100 text-green-800',
            user_rejected: 'bg-red-100 text-red-800',
            payment_pending: 'bg-yellow-100 text-yellow-800',
            payment_completed: 'bg-green-100 text-green-800',
            order_placed: 'bg-blue-100 text-blue-800',
            order_under_process: 'bg-blue-100 text-blue-800',
            ready_for_pickup: 'bg-purple-100 text-purple-800',
            out_for_delivery: 'bg-indigo-100 text-indigo-800',
            at_delivery_point: 'bg-indigo-100 text-indigo-800',
            delivered: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800',
            refund_initiated: 'bg-yellow-100 text-yellow-800',
            refund_processing: 'bg-blue-100 text-blue-800',
            refund_completed: 'bg-green-100 text-green-800',
            refund_failed: 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusIcon = (status) => {
        if (!status) return <Clock className="w-4 h-4" />;
        if (status === 'delivered' || status === 'payment_completed' || status === 'refund_completed') return <CheckCircle className="w-4 h-4" />;
        if (status === 'cancelled' || status === 'shop_rejected' || status === 'user_rejected' || status === 'refund_failed') return <XCircle className="w-4 h-4" />;
        if (status === 'out_for_delivery' || status === 'ready_for_pickup') return <Package className="w-4 h-4" />;
        if (status === 'pending_shop_confirmation' || status === 'payment_pending') return <AlertCircle className="w-4 h-4" />;
        return <Clock className="w-4 h-4" />;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-xl text-gray-600">Loading CTO Dashboard...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-3 rounded-lg shadow-lg">
                            <TrendingUp className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">CTO Dashboard</h1>
                            <p className="text-gray-600 mt-1">Business Metrics and Key Performance Indicators</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-3">
                        <Clock className="w-4 h-4" />
                        <span>Last updated: {new Date().toLocaleString()}</span>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r mb-6 shadow-sm">
                        <div className="flex items-center">
                            <AlertCircle className="w-5 h-5 mr-2" />
                            {error}
                        </div>
                    </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {/* Contact Us Count */}
                    <div 
                        className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105 cursor-pointer"
                        onClick={() => navigate('/contact')}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <Mail className="w-10 h-10" />
                            <ExternalLink className="w-5 h-5 opacity-70" />
                        </div>
                        <div>
                            <p className="text-blue-100 text-sm font-medium">Contact Requests</p>
                            <p className="text-4xl font-bold mt-2">{stats.contactUsCount}</p>
                            <p className="text-blue-100 text-xs mt-1">Click to view details</p>
                        </div>
                    </div>

                    {/* Total Orders */}
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                        <div className="flex items-center justify-between mb-4">
                            <ShoppingCart className="w-10 h-10" />
                        </div>
                        <div>
                            <p className="text-green-100 text-sm font-medium">Total Orders</p>
                            <p className="text-4xl font-bold mt-2">{stats.totalOrders}</p>
                            <p className="text-green-100 text-xs mt-1">All time orders</p>
                        </div>
                    </div>

                    {/* KDP App Installs */}
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                        <div className="flex items-center justify-between mb-4">
                            <Smartphone className="w-10 h-10" />
                            <Download className="w-5 h-5 opacity-70" />
                        </div>
                        <div>
                            <p className="text-purple-100 text-sm font-medium">KDP App Installs</p>
                            <p className="text-4xl font-bold mt-2">{stats.kdpInstalls}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-purple-100">
                                <span>📱 Android: {stats.kdpAndroidInstalls}</span>
                                <span>🍎 iOS: {stats.kdpIOSInstalls}</span>
                            </div>
                        </div>
                    </div>

                    {/* KD App Installs */}
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                        <div className="flex items-center justify-between mb-4">
                            <Smartphone className="w-10 h-10" />
                            <Download className="w-5 h-5 opacity-70" />
                        </div>
                        <div>
                            <p className="text-orange-100 text-sm font-medium">KD App Installs</p>
                            <p className="text-4xl font-bold mt-2">{stats.kdInstalls}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-orange-100">
                                <span>📱 Android: {stats.kdAndroidInstalls}</span>
                                <span>🍎 iOS: {stats.kdIOSInstalls}</span>
                            </div>
                        </div>
                    </div>

                    {/* Web Users */}
                    <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                        <div className="flex items-center justify-between mb-4">
                            <Users className="w-10 h-10" />
                        </div>
                        <div>
                            <p className="text-cyan-100 text-sm font-medium">Web Users</p>
                            <p className="text-4xl font-bold mt-2">{stats.webUsers}</p>
                            <p className="text-cyan-100 text-xs mt-1">Website visitors</p>
                        </div>
                    </div>

                    {/* Total Installs */}
                    <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                        <div className="flex items-center justify-between mb-4">
                            <TrendingUp className="w-10 h-10" />
                        </div>
                        <div>
                            <p className="text-pink-100 text-sm font-medium">Total Installs</p>
                            <p className="text-4xl font-bold mt-2">{stats.totalInstalls}</p>
                            <p className="text-pink-100 text-xs mt-1">All platforms combined</p>
                        </div>
                    </div>
                </div>

                {/* Recent Orders Section */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div 
                        className="flex items-center justify-between mb-6 cursor-pointer"
                        onClick={() => setOrdersExpanded(!ordersExpanded)}
                    >
                        <div className="flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-indigo-600" />
                            <h2 className="text-xl font-semibold text-gray-800">Recent Orders</h2>
                            <span className="text-sm text-gray-500">({recentOrders.length})</span>
                        </div>
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition">
                            {ordersExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                    </div>

                    {ordersExpanded && (
                        <div className="space-y-3">
                            {recentOrders.length === 0 ? (
                                <div className="text-center py-12">
                                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg">No recent orders</p>
                                </div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shop</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {recentOrders.map((order) => (
                                                    <tr key={order._id} className="hover:bg-gray-50 transition">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                            #{order.orderId}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            <div className="flex items-center gap-2">
                                                                <Users className="w-4 h-4 text-gray-400" />
                                                                {order.userId?.phone || order.userId?.name || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {order.shopId?.name || order.shopId?.shopName || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {formatDate(order.createdAt)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                                                {getStatusIcon(order.status)}
                                                                {order.status?.replace(/_/g, ' ')}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                                                            {formatCurrency(order.grandTotal)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="mt-4 flex justify-end">
                                        <button
                                            onClick={() => navigate('/orderandinvoices')}
                                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                                        >
                                            View All Orders
                                            <ExternalLink className="w-4 h-4" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

              
            </div>
        </div>
    );
};

export default CTODashboard;
